import { Geometry, Point, LineString, Polygon } from 'ol/geom';
import {
  getPolygonWarnings,
  getLineStringWarnings,
} from './utils/hasSelfIntersection';
import { transform } from 'ol/proj';

export type GeometryValidationResult = {
  isValid: boolean;
  reason: string;
  warning?: string;
};

export function isValidGeometry(
  geometry: Geometry | null | undefined,
  maxPoints?: number | undefined,
): GeometryValidationResult {
  if (!geometry) {
    return { isValid: false, reason: 'No shape drawn.' };
  }

  let coordinates: number[] | number[][];
  let isValid: boolean;
  let reason = '';
  let warning: string | undefined;

  switch (geometry.getType()) {
    case 'Point':
      coordinates = (geometry as Point).getCoordinates();
      isValid = true; // A Point is always valid
      reason = '';
      break;
    case 'LineString': {
      const lineString = geometry as LineString;
      coordinates = lineString.getCoordinates();

      if (maxPoints) {
        isValid = coordinates.length >= 2 && coordinates.length <= maxPoints;
        reason = isValid
          ? ''
          : coordinates.length < 2
            ? 'A line needs at least 2 points.'
            : `This line must have at maximum ${maxPoints} points.`;
      } else {
        isValid = coordinates.length >= 2; // A LineString needs at least 2 points
        reason = isValid ? '' : 'A line needs at least 2 points.';
      }

      // Check for excessive longitude span if the line is otherwise valid
      if (isValid) {
        // Transform from Web Mercator to geographic coordinates for validation
        // Use transform() instead of toLonLat() to preserve extended coordinates (±540°)
        const geoCoordinates = lineString
          .getCoordinates()
          .map(
            (coord) => transform(coord, 'EPSG:3857', 'EPSG:4326') as number[],
          );
        warning = getLineStringWarnings(geoCoordinates) ?? undefined;
      }

      break;
    }
    case 'Polygon': {
      const polygon = geometry as Polygon;
      coordinates = polygon.getCoordinates()[0];
      isValid =
        coordinates.length >= 4 && // A Polygon needs at least 4 points (first and last point should be the same)
        coordinates[0][0] === coordinates[coordinates.length - 1][0] &&
        coordinates[0][1] === coordinates[coordinates.length - 1][1];
      reason = isValid
        ? ''
        : coordinates.length < 4
          ? 'A polygon needs at least 4 points, including the closing point.'
          : 'The first and last points of a polygon must be the same.';

      // Check for self-intersections and excessive longitude span if the polygon is otherwise valid
      if (isValid) {
        // Transform from Web Mercator to geographic coordinates for validation
        // Use transform() instead of toLonLat() to preserve extended coordinates (±540°)
        const geoCoordinates = polygon
          .getCoordinates()
          .map((ring) =>
            ring.map(
              (coord) => transform(coord, 'EPSG:3857', 'EPSG:4326') as number[],
            ),
          );
        warning = getPolygonWarnings(geoCoordinates) ?? undefined;
      }
      break;
    }
    case 'Circle':
      isValid = true; // A Circle is considered valid if it exists
      reason = '';
      break;
    default:
      isValid = false;
      reason = 'Unknown geometry type.';
  }

  return { isValid, reason, warning };
}
